import React from 'react';
import ClientJourneyMilestones from './ClientJourneyMilestones';
import { getFirstName } from '../../shared/utils/nameHelpers';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Stack,
  Button,
  Divider,
  Paper,
  useTheme,
  alpha,
  keyframes,
  Fade,
  Zoom
} from '@mui/material';
import {
  TrendingUp,
  CalendarToday,
  Assignment,
  EmojiEvents,
  DirectionsRun,
  FitnessCenter,
  Star,
  Favorite,
  Event
} from '@mui/icons-material';
import { formatDateSafe } from '../../shared/utils/dateHelpers';

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

interface Visit {
  duration_minutes: number;
  late_cancel: boolean;
  location_name: string;
  no_show: boolean;
  pricing_option: string;
  service_category: string;
  service_type: string;
  session_date: string;
  staff_name: string;
}

interface ClientJourneyOverviewProps {
  client: any;
  milestones: any[];
  assessmentData: {
    preHealthConsult: boolean;
    healthConsult: boolean;
    intakeForm: boolean;
    initialReports: {
      inbody: boolean;
      movement: boolean;
      metabolic: boolean;
      nutrition: boolean;
      training: boolean;
    };
    reviewReports: {
      inbody: boolean;
      movement: boolean;
      metabolic: boolean;
      nutrition: boolean;
      training: boolean;
    };
  };
  onGenerateReport?: (type: 'Initial' | 'Review') => void;
  upcomingAppointments?: any[];
}

const ClientJourneyOverview: React.FC<ClientJourneyOverviewProps> = ({
  client,
  milestones,
  assessmentData,
  upcomingAppointments = []
}) => {
  const theme = useTheme();

  // Calculate overall progress
  const calculateOverallProgress = () => {
    const preAssessmentSteps = [
      assessmentData.preHealthConsult,
      assessmentData.healthConsult,
      assessmentData.intakeForm
    ];
    const initialAssessmentSteps = Object.values(assessmentData.initialReports);
    const reviewAssessmentSteps = Object.values(assessmentData.reviewReports);
    
    const totalSteps = preAssessmentSteps.length + initialAssessmentSteps.length + reviewAssessmentSteps.length;
    const completedSteps = [...preAssessmentSteps, ...initialAssessmentSteps, ...reviewAssessmentSteps].filter(Boolean).length;
    
    return (completedSteps / totalSteps) * 100;
  };

  // Calculate health score based on multiple factors
  const calculateHealthScore = () => {
    let score = 0;
    
    // 1. Base 50 points for having an active programme
    if (client.active_programme) {
      score += 50;
    }
    
    // Get attended visits only (no_show = false)
    const attendedVisits = client.visit_history?.filter((visit: Visit) => !visit.no_show) || [];
    
    // 2. Up to 10 points for visit recency (sliding scale: 14 days = 0 points, 1 day = 10 points)
    if (attendedVisits.length > 0) {
      const lastVisitDate = new Date(attendedVisits[attendedVisits.length - 1].session_date);
      const daysSinceLastVisit = Math.floor((Date.now() - lastVisitDate.getTime()) / (24 * 60 * 60 * 1000));
      
      if (daysSinceLastVisit <= 14) {
        // Linear scale from 10 points (1 day) to 0 points (14 days)
        const recencyScore = Math.max(0, 10 - (daysSinceLastVisit - 1) * (10 / 13));
        score += recencyScore;
      }
    }
    
    // 3. Up to 10 points for attending specific visit types
    const qualifyingVisits = attendedVisits.filter((visit: Visit) => 
      visit.service_type !== "Personal Training" && 
      visit.service_type !== "UFIT Programme"
    );
    
    if (qualifyingVisits.length > 0) {
      score += 5; // 5 points for any qualifying visit
      
      // Check if any qualifying visit was within last 12 weeks
      const twelveWeeksAgo = new Date();
      twelveWeeksAgo.setDate(twelveWeeksAgo.getDate() - 84); // 12 weeks = 84 days
      
      const recentQualifyingVisit = qualifyingVisits.some((visit: Visit) => 
        new Date(visit.session_date) > twelveWeeksAgo
      );
      
      if (recentQualifyingVisit) {
        score += 5; // Additional 5 points if within last 12 weeks
      }
    }
    
    // 4. Up to 10 points for subsequent purchase
    if (client.subsequent_products && client.subsequent_products.length > 0) {
      score += 5; // 5 points for any purchase
      
      // Check if any product contains "week programme"
      const hasWeekProgramme = client.subsequent_products.some((product: string) => 
        product.toLowerCase().includes('week programme')
      );
      
      if (hasWeekProgramme) {
        score += 5; // Additional 5 points for "week programme" purchase
      }
    }
    
    // 5. Up to 10 points for visit frequency in last 6 weeks
    const sixWeeksAgo = new Date();
    sixWeeksAgo.setDate(sixWeeksAgo.getDate() - 42); // 6 weeks = 42 days
    
    // Determine the period to consider
    let periodStart = sixWeeksAgo;
    if (client.first_session_date) {
      const firstSessionDate = new Date(client.first_session_date);
      if (firstSessionDate > sixWeeksAgo) {
        periodStart = firstSessionDate;
      }
    } else if (attendedVisits.length > 0) {
      // If no first_session_date, use the earliest visit
      const firstVisitDate = new Date(attendedVisits[0].session_date);
      if (firstVisitDate > sixWeeksAgo) {
        periodStart = firstVisitDate;
      }
    }
    
    const visitsInPeriod = attendedVisits.filter((visit: Visit) => 
      new Date(visit.session_date) >= periodStart
    );
    
    const weeksSincePeriodStart = Math.max(1, Math.floor((Date.now() - periodStart.getTime()) / (7 * 24 * 60 * 60 * 1000)));
    const visitsPerWeek = visitsInPeriod.length / weeksSincePeriodStart;
    
    if (visitsPerWeek >= 3) {
      score += 10; // 10 points for 3+ visits per week
    } else {
      score += (visitsPerWeek / 3) * 10; // Proportional score for less than 3 visits/week
    }
    
    // 6. Up to 10 points for frequency ratio score
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    const visitsLast2Weeks = attendedVisits.filter((visit: Visit) => 
      new Date(visit.session_date) >= twoWeeksAgo
    ).length;
    
    const visitsLast6Weeks = attendedVisits.filter((visit: Visit) => 
      new Date(visit.session_date) >= sixWeeksAgo
    ).length;
    
    if (visitsLast6Weeks > 0) {
      const frequencyRatio = visitsLast2Weeks / (visitsLast6Weeks / 3);
      const cappedRatio = Math.min(frequencyRatio, 1);
      score += cappedRatio * 10;
    }
    
    return Math.round(Math.min(score, 100));
  };

  const healthScore = calculateHealthScore();
  const progress = calculateOverallProgress();

  // Get health score color based on value
  const getHealthScoreColor = () => {
    if (healthScore < 50) return theme.palette.error.main; // Red
    if (healthScore >= 50 && healthScore < 70) return theme.palette.warning.main; // Yellow
    return theme.palette.success.main; // Green
  };

  const healthScoreColor = getHealthScoreColor();

  // Get next appointment
  const nextAppointment = upcomingAppointments?.[0];

  // Quick stats
  const attendedVisitsForStats = client.visit_history?.filter((visit: Visit) => !visit.no_show) || [];
  const totalSessions = attendedVisitsForStats.length;
  const weeksSinceStart = client.first_session_date
    ? Math.floor((Date.now() - new Date(client.first_session_date).getTime()) / (7 * 24 * 60 * 60 * 1000))
    : 0;

  return (
    <Box>
      {/* Welcome Section */}
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            borderRadius: 3,
            p: 4,
            mb: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {getFirstName(client.client_name)}'s Fitness Journey
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Member since {formatDateSafe(client.first_session_date, 'MMMM yyyy')} • {weeksSinceStart} weeks of progress
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Chip
                    icon={<Star />}
                    label={`${totalSessions} Sessions Completed`}
                    color="primary"
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Chip
                    icon={<EmojiEvents />}
                    label={client.active_programme ? 'Active Programme' : 'Programme Ended'}
                    color={client.active_programme ? 'success' : 'default'}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: `conic-gradient(${healthScoreColor} ${healthScore * 3.6}deg, ${alpha(theme.palette.grey[300], 0.3)} 0deg)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1,
                      animation: `${pulse} 2s ease-in-out infinite`
                    }}
                  >
                    <Paper
                      elevation={3}
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'white'
                      }}
                    >
                      <Typography variant="h3" fontWeight="bold" sx={{ color: healthScoreColor }}>
                        {healthScore}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Health Score
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: alpha(theme.palette.primary.main, 0.1),
              filter: 'blur(40px)'
            }}
          />
        </Paper>
      </Fade>


      {/* Progress Overview */}
      <Grid container spacing={3}>
        {/* Next Appointment Card */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={900}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%', 
                borderRadius: 2,
                background: nextAppointment 
                  ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
                  : 'inherit'
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Event sx={{ mr: 1, color: 'success.main' }} />
                  Next Appointment
                </Typography>
                {nextAppointment ? (
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="success.main" gutterBottom>
                      {formatDateSafe(new Date(nextAppointment.date.value), 'EEE, MMM d')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {formatDateSafe(new Date(nextAppointment.date.value), 'h:mm a')}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>Coach:</strong> {nextAppointment.coach}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Location:</strong> {nextAppointment.location}
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="text.secondary">
                      No upcoming appointments
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        {/* Assessment Progress Card */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={1000}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Assignment sx={{ mr: 1, color: 'primary.main' }} />
                  Assessment Progress
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Overall Progress</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {Math.round(progress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                      }
                    }}
                  />
                </Box>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Pre-Assessment</Typography>
                    <Chip
                      size="small"
                      label={
                        [assessmentData.preHealthConsult, assessmentData.healthConsult, assessmentData.intakeForm]
                          .filter(Boolean).length + '/3'
                      }
                      color={
                        [assessmentData.preHealthConsult, assessmentData.healthConsult, assessmentData.intakeForm]
                          .filter(Boolean).length === 3 ? 'success' : 'warning'
                      }
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Initial Assessment</Typography>
                    <Chip
                      size="small"
                      label={
                        Object.values(assessmentData.initialReports).filter(Boolean).length + '/5'
                      }
                      color={
                        Object.values(assessmentData.initialReports).filter(Boolean).length === 5 ? 'success' : 'warning'
                      }
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Review Assessment</Typography>
                    <Chip
                      size="small"
                      label={
                        Object.values(assessmentData.reviewReports).filter(Boolean).length + '/5'
                      }
                      color={
                        Object.values(assessmentData.reviewReports).filter(Boolean).length === 5 ? 'success' : 'default'
                      }
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        {/* Key Metrics Card */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={1100}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ mr: 1, color: 'secondary.main' }} />
                  Key Metrics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }}
                    >
                      <FitnessCenter sx={{ fontSize: 30, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h5" fontWeight="bold">
                        {totalSessions}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Sessions
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.05)
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 30, color: 'secondary.main', mb: 1 }} />
                      <Typography variant="h5" fontWeight="bold">
                        {weeksSinceStart}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Weeks Active
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.success.main, 0.05)
                      }}
                    >
                      <DirectionsRun sx={{ fontSize: 30, color: 'success.main', mb: 1 }} />
                      <Typography variant="h5" fontWeight="bold">
                        {Math.round(totalSessions / Math.max(weeksSinceStart, 1))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sessions/Week
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(healthScoreColor, 0.05)
                      }}
                    >
                      <Favorite sx={{ fontSize: 30, color: healthScoreColor, mb: 1 }} />
                      <Typography variant="h5" fontWeight="bold" sx={{ color: healthScoreColor }}>
                        {healthScore}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Health Score
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Journey Milestones */}
      <Box sx={{ mt: 3 }}>
        <ClientJourneyMilestones milestones={milestones} client={client} />
      </Box>
    </Box>
  );
};

export default ClientJourneyOverview;